// src/browser.ts
import { parse as parseYaml } from "yaml";

// src/defaults.ts
var DEFAULT_CONSTRAINTS = {
  fieldTypes: {
    text: "off",
    number: "off",
    boolean: "off",
    staticEnum: "off",
    dynamicEnum: "off",
    dynamicSchema: "off",
    array: "off",
    object: "off"
  },
  layout: {
    group: "off",
    conditionals: "off",
    maxNestingDepth: Infinity
  },
  uiSchema: {
    layouts: {
      VerticalLayout: "off",
      HorizontalLayout: "off",
      Group: "off",
      Categorization: "off",
      Category: "off"
    },
    rules: {
      enabled: "off",
      effects: {
        SHOW: "off",
        HIDE: "off",
        ENABLE: "off",
        DISABLE: "off"
      }
    }
  },
  fieldOptions: {
    label: "off",
    placeholder: "off",
    required: "off",
    minValue: "off",
    maxValue: "off",
    minItems: "off",
    maxItems: "off"
  },
  controlOptions: {
    format: "off",
    readonly: "off",
    multi: "off",
    showUnfocusedDescription: "off",
    hideRequiredAsterisk: "off",
    custom: {}
  }
};
var DEFAULT_CONFIG = {
  constraints: DEFAULT_CONSTRAINTS
};
function mergeWithDefaults(config) {
  if (!config) {
    return DEFAULT_CONSTRAINTS;
  }
  return {
    fieldTypes: {
      ...DEFAULT_CONSTRAINTS.fieldTypes,
      ...config.fieldTypes
    },
    layout: {
      ...DEFAULT_CONSTRAINTS.layout,
      ...config.layout
    },
    uiSchema: {
      layouts: {
        ...DEFAULT_CONSTRAINTS.uiSchema.layouts,
        ...config.uiSchema?.layouts
      },
      rules: {
        enabled: config.uiSchema?.rules?.enabled ?? DEFAULT_CONSTRAINTS.uiSchema.rules.enabled,
        effects: {
          ...DEFAULT_CONSTRAINTS.uiSchema.rules.effects,
          ...config.uiSchema?.rules?.effects
        }
      }
    },
    fieldOptions: {
      ...DEFAULT_CONSTRAINTS.fieldOptions,
      ...config.fieldOptions
    },
    controlOptions: {
      ...DEFAULT_CONSTRAINTS.controlOptions,
      ...config.controlOptions,
      custom: {
        ...DEFAULT_CONSTRAINTS.controlOptions.custom,
        ...config.controlOptions?.custom
      }
    }
  };
}

// src/validators/field-types.ts
var FIELD_TYPE_MAP = {
  text: "text",
  number: "number",
  boolean: "boolean",
  enum: "staticEnum",
  dynamic_enum: "dynamicEnum",
  dynamic_schema: "dynamicSchema",
  array: "array",
  object: "object"
};
var FIELD_TYPE_NAMES = {
  text: "text field",
  number: "number field",
  boolean: "boolean field",
  enum: "static enum field",
  dynamic_enum: "dynamic enum field",
  dynamic_schema: "dynamic schema field",
  array: "array field",
  object: "object field"
};
function validateFieldTypes(context, constraints) {
  const issues = [];
  const constraintKey = FIELD_TYPE_MAP[context.fieldType];
  if (!constraintKey) {
    return issues;
  }
  const severity = constraints[constraintKey];
  if (severity && severity !== "off") {
    const fieldTypeName = FIELD_TYPE_NAMES[context.fieldType] ?? context.fieldType;
    issues.push(createFieldTypeIssue(context, fieldTypeName, severity));
  }
  return issues;
}
function createFieldTypeIssue(context, fieldTypeName, severity) {
  const path = context.path ?? context.fieldName;
  return {
    code: "DISALLOWED_FIELD_TYPE",
    message: `Field "${context.fieldName}" uses ${fieldTypeName}, which is not allowed in this project`,
    severity: severity === "error" ? "error" : "warning",
    category: "fieldTypes",
    path,
    fieldName: context.fieldName,
    fieldType: context.fieldType
  };
}
function isFieldTypeAllowed(fieldType, constraints) {
  const constraintKey = FIELD_TYPE_MAP[fieldType];
  if (!constraintKey) {
    return true;
  }
  const severity = constraints[constraintKey];
  return !severity || severity === "off";
}
function getFieldTypeSeverity(fieldType, constraints) {
  const constraintKey = FIELD_TYPE_MAP[fieldType];
  if (!constraintKey) {
    return "off";
  }
  return constraints[constraintKey] ?? "off";
}

// src/validators/layout.ts
function validateLayout(context, constraints) {
  const issues = [];
  if (context.layoutType === "group") {
    const groupSeverity = constraints.group;
    if (groupSeverity && groupSeverity !== "off") {
      issues.push(createGroupIssue(context, groupSeverity));
    }
  }
  if (context.layoutType === "conditional") {
    const conditionalSeverity = constraints.conditionals;
    if (conditionalSeverity && conditionalSeverity !== "off") {
      issues.push(createConditionalIssue(context, conditionalSeverity));
    }
  }
  const maxDepth = constraints.maxNestingDepth;
  if (maxDepth !== void 0 && context.depth > maxDepth) {
    issues.push(createNestingDepthIssue(context, maxDepth));
  }
  return issues;
}
function createGroupIssue(context, severity) {
  const labelInfo = context.label ? ` "${context.label}"` : "";
  const issue = {
    code: "DISALLOWED_GROUP",
    message: `Group${labelInfo} is not allowed - visual grouping is not supported in this project`,
    severity: severity === "error" ? "error" : "warning",
    category: "layout"
  };
  if (context.path !== void 0) {
    issue.path = context.path;
  }
  return issue;
}
function createConditionalIssue(context, severity) {
  const issue = {
    code: "DISALLOWED_CONDITIONAL",
    message: `Conditional visibility (when/is) is not allowed in this project`,
    severity: severity === "error" ? "error" : "warning",
    category: "layout"
  };
  if (context.path !== void 0) {
    issue.path = context.path;
  }
  return issue;
}
function createNestingDepthIssue(context, maxDepth) {
  const issue = {
    code: "EXCEEDED_NESTING_DEPTH",
    message: `Nesting depth ${String(context.depth)} exceeds maximum allowed depth of ${String(maxDepth)}`,
    severity: "error",
    category: "layout"
  };
  if (context.path !== void 0) {
    issue.path = context.path;
  }
  return issue;
}
function isLayoutTypeAllowed(layoutType, constraints) {
  if (layoutType === "group") {
    const severity2 = constraints.group;
    return !severity2 || severity2 === "off";
  }
  const severity = constraints.conditionals;
  return !severity || severity === "off";
}
function isNestingDepthAllowed(depth, constraints) {
  const maxDepth = constraints.maxNestingDepth;
  if (maxDepth === void 0) {
    return true;
  }
  return depth <= maxDepth;
}

// src/validators/field-options.ts
function validateFieldOptions(context, constraints) {
  const issues = [];
  for (const option of context.presentOptions) {
    const severity = constraints[option];
    if (severity && severity !== "off") {
      issues.push(createFieldOptionIssue(context, option, severity));
    }
  }
  return issues;
}
function createFieldOptionIssue(context, option, severity) {
  const path = context.path ?? context.fieldName;
  return {
    code: "DISALLOWED_FIELD_OPTION",
    message: `Field "${context.fieldName}" uses the "${option}" option, which is not allowed in this project`,
    severity: severity === "error" ? "error" : "warning",
    category: "fieldOptions",
    path,
    fieldName: context.fieldName
  };
}
function extractFieldOptions(field) {
  const options = [];
  if (field["label"] !== void 0) options.push("label");
  if (field["placeholder"] !== void 0) options.push("placeholder");
  if (field["required"] !== void 0) options.push("required");
  if (field["min"] !== void 0 || field["minValue"] !== void 0) options.push("minValue");
  if (field["max"] !== void 0 || field["maxValue"] !== void 0) options.push("maxValue");
  if (field["minItems"] !== void 0) options.push("minItems");
  if (field["maxItems"] !== void 0) options.push("maxItems");
  return options;
}
function isFieldOptionAllowed(option, constraints) {
  const severity = constraints[option];
  return !severity || severity === "off";
}
function getFieldOptionSeverity(option, constraints) {
  return constraints[option] ?? "off";
}

// src/validators/formspec.ts
function validateFormSpecElements(elements, options = {}) {
  const constraints = mergeWithDefaults(options.constraints);
  const issues = [];
  walkElements(elements, constraints, issues, "", 0);
  return {
    valid: !issues.some((issue) => issue.severity === "error"),
    issues
  };
}
function validateFormSpec(formSpec, options = {}) {
  return validateFormSpecElements(formSpec.elements, options);
}
function walkElements(elements, constraints, issues, pathPrefix, depth) {
  for (const element of elements) {
    const elementPath = pathPrefix;
    if (element._type === "field") {
      validateField(element, constraints, issues, elementPath, depth);
    } else if (element._type === "group") {
      validateGroup(element, constraints, issues, elementPath, depth);
    } else {
      validateConditional(element, constraints, issues, elementPath, depth);
    }
  }
}
function validateField(field, constraints, issues, pathPrefix, depth) {
  const fieldPath = pathPrefix ? `${pathPrefix}/${field.name}` : field.name;
  const fieldTypeIssues = validateFieldTypes(
    {
      fieldType: field._field,
      fieldName: field.name,
      path: fieldPath
    },
    constraints.fieldTypes
  );
  issues.push(...fieldTypeIssues);
  const presentOptions = extractFieldOptions(field);
  if (presentOptions.length > 0) {
    const optionIssues = validateFieldOptions(
      {
        fieldName: field.name,
        presentOptions,
        path: fieldPath
      },
      constraints.fieldOptions
    );
    issues.push(...optionIssues);
  }
  if (field._field === "array" || field._field === "object") {
    const layoutIssues = validateLayout(
      {
        layoutType: "group",
        // Arrays/objects contribute to nesting depth
        depth: depth + 1,
        path: fieldPath
      },
      constraints.layout
    );
    issues.push(...layoutIssues.filter((issue) => issue.code === "EXCEEDED_NESTING_DEPTH"));
    if (field._field === "array" && "items" in field) {
      walkElements(
        field.items,
        constraints,
        issues,
        `${fieldPath}[]`,
        depth + 1
      );
    } else if ("properties" in field) {
      walkElements(
        field.properties,
        constraints,
        issues,
        fieldPath,
        depth + 1
      );
    }
  }
}
function validateGroup(group, constraints, issues, pathPrefix, depth) {
  const groupPath = pathPrefix ? `${pathPrefix}/[group:${group.label}]` : `[group:${group.label}]`;
  const layoutIssues = validateLayout(
    {
      layoutType: "group",
      label: group.label,
      depth,
      path: groupPath
    },
    constraints.layout
  );
  issues.push(...layoutIssues);
  walkElements(group.elements, constraints, issues, pathPrefix, depth);
}
function validateConditional(conditional, constraints, issues, pathPrefix, depth) {
  const condPath = pathPrefix ? `${pathPrefix}/[when:${conditional.field}=${String(conditional.value)}]` : `[when:${conditional.field}=${String(conditional.value)}]`;
  const layoutIssues = validateLayout(
    {
      layoutType: "conditional",
      depth,
      path: condPath
    },
    constraints.layout
  );
  issues.push(...layoutIssues);
  walkElements(conditional.elements, constraints, issues, pathPrefix, depth);
}

// src/browser.ts
function loadConfigFromString(yamlContent) {
  const parsed = parseYaml(yamlContent);
  if (parsed === null || parsed === void 0) {
    return mergeWithDefaults(void 0);
  }
  if (typeof parsed !== "object" || Array.isArray(parsed)) {
    throw new Error(`Invalid config content: expected an object, got ${typeof parsed}`);
  }
  return mergeWithDefaults(parsed.constraints);
}
function defineConstraints(config) {
  return mergeWithDefaults(config);
}
export {
  DEFAULT_CONFIG,
  DEFAULT_CONSTRAINTS,
  defineConstraints,
  extractFieldOptions,
  getFieldOptionSeverity,
  getFieldTypeSeverity,
  isFieldOptionAllowed,
  isFieldTypeAllowed,
  isLayoutTypeAllowed,
  isNestingDepthAllowed,
  loadConfigFromString,
  mergeWithDefaults,
  validateFieldOptions,
  validateFieldTypes,
  validateFormSpec,
  validateFormSpecElements,
  validateLayout
};
//# sourceMappingURL=browser.js.map